import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';

import { StyledOcticon, Timeline, useConfirm } from '@primer/react';
import { VariableSizeList } from 'react-window';

import parseISO from 'date-fns/parseISO';

import classNames from 'classnames';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

import ActivityItem, { LoadingActivityItem } from './ActivityItem/ActivityItem';
import Avatar from '../common/Avatar/Avatar';
import CollapsibleCardView from '../common/CardView/CollapsibleCardView';
import CommentIcon from '../common/icons/CommentIcon';
import ErrorLabel from '../common/Labels/ErrorLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import TextInputWithError from '../common/TextInputs/TextInputWithError/TextInputWithError';
import VerticalGradientOverlay from '../common/overlays/VerticalGradientOverlay';

const sortActivityData = activityData => activityData
  ? reverse(sortBy(activityData, activityEvent => parseISO(activityEvent.eventDate)))
  : activityData;

const ActivityItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <ActivityItem {...props} />
  </div>
))

const LoadingActivityItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <LoadingActivityItem {...props} />
  </div>
))

const ActivityRow = ({
  index,
  style,
  data
}) => {
  const {
    sortedActivityData,
    isItemLoaded,
    listRef,
    rowHeights,
    showCondensed,
    currentUser,
    editModeCommentID,
    handleEnterEditMode,
    handleDeleteComment
  } = data;
  const rowRef = useRef({});

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);

  if (!isItemLoaded(index)) {
    return <LoadingActivityItemWithRef ref={rowRef} condensed={showCondensed} />
  }

  return (
    <div style={style}>
      <ActivityItemWithRef
        ref={rowRef}
        key={sortedActivityData[index].id}
        entry={sortedActivityData[index]}
        condensed={showCondensed}
        currentUser={currentUser}
        editModeCommentID={editModeCommentID}
        handleEnterEditMode={handleEnterEditMode}
        handleDeleteComment={handleDeleteComment}
      />
    </div>
  )
};

const ActivityTimeline = ({
  width,
  height,
  sortedActivityData,
  isItemLoaded,
  itemCount,
  onLoadMoreComments,
  listRef,
  getRowHeight,
  rowHeights,
  showCondensed,
  currentUser,
  editModeCommentID,
  handleEnterEditMode,
  handleDeleteComment
}) => {
  if (!sortedActivityData?.length) {
    return null;
  }

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={onLoadMoreComments}
      minimumBatchSize={5}
      threshold={5}
    >
      {({ onItemsRendered, ref }) => (
        <VariableSizeList
          ref={(list) => {
            // Pass List ref through to InfiniteLoader
            ref(list);

            // And store a copy for yourself.
            listRef.current = list;
          }}
          height={height}
          onItemsRendered={onItemsRendered}
          itemCount={sortedActivityData.length}
          estimatedItemSize={56}
          itemSize={getRowHeight}
          width={width}
          itemData={{
            sortedActivityData,
            isItemLoaded,
            listRef,
            rowHeights,
            showCondensed,
            currentUser,
            editModeCommentID,
            handleEnterEditMode,
            handleDeleteComment
          }}
        >
          {ActivityRow}
        </VariableSizeList>
      )}
    </InfiniteLoader>
  )
};

export const ActivityLog = ({
  id,
  className,
  activityData,
  modelID,
  modelVersion,
  pipelineID,
  areCommentsLoading,
  commentError,
  currentUser,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  canLoadMoreComments,
  onLoadMoreComments,
  height = 240,
  showCondensed = false,
  disableScrollFading = false,
}) => {
  const [messageText, setMessageText] = useState('');
  const [editModeCommentID, setEditModeCommentID] = useState(null);

  const inputRef = useRef(null);
  const listRef = useRef({});
  const rowHeights = useRef({});

  const areCommentsNull = activityData === null;
  const sortedActivityData = useMemo(() => {
    if (areCommentsNull) {
      return null;
    }

    return sortActivityData(activityData);
  }, [activityData, areCommentsNull]);

  const confirm = useConfirm();

  const activityLength = sortedActivityData?.length || 0;

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = canLoadMoreComments ? activityLength + 1 : activityLength;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !canLoadMoreComments || index < activityLength;

  const modelDisplayName = (modelID && modelVersion) ? `${modelID} ${modelVersion}` : undefined;

  const noDataMessage = commentError
    ? `There was an error retrieving activity data${modelID ? ' for this model' : pipelineID ? ' for this pipeline' : ''}...`
    : `No activity to show${modelID ? ' for this model' : pipelineID ? ' for this pipeline' : ''}`;

  const displayMessage = areCommentsLoading ? 'Loading activity data\u2026' : noDataMessage;

  const onSubmit = (value) => {
    if (value && value?.trim()?.length) {
      if (editModeCommentID) {
        onUpdateComment(editModeCommentID, value);
        setEditModeCommentID(null);
      } else {
        onAddComment(value);
      }
      setMessageText('');
    }
  };

  const handleEnterEditMode = useCallback((commentID) => {
    const comment = activityData.find(commentInstance => commentInstance.id === commentID);
    if (!comment || !inputRef.current) {
      return;
    }

    setEditModeCommentID(commentID);
    setMessageText(comment?.content?.message);
    inputRef.current.focus();
  }, [activityData]);

  const handleDeleteComment = useCallback(async (commentID) => {
    const confirmOptions = {
      title: 'Delete Comment',
      content: 'Are you sure you want to delete this comment?',
      confirmButtonContent: 'Delete',
      confirmButtonType: 'danger'
    };

    if (await confirm(confirmOptions)) {
      onDeleteComment(commentID);
    }
  }, [confirm, onDeleteComment]);

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 106;
  }, [rowHeights]);

  return (
    <div id={id} className="relative mx-0 px-0">
      {
        areCommentsLoading ? (
          <LoadingSpinner animationKey="activity-log-loader" useOverlay />
        ) : null
      }
      <CollapsibleCardView className={classNames(className)} icon={CommentIcon} headerText="Activity">
        {!sortedActivityData?.length ? (
          <div className="p-4 flex-grow flex flex-col justify-center items-center">
            <StyledOcticon
              className="mb-2 text-brand"
              icon={CommentIcon}
              size={16}
            />
            <ErrorLabel>{displayMessage}</ErrorLabel>
          </div>
        ) : (
          <div id={`${id}-scroll-box`} className="overflow-scroll px-6 flex-grow">
            {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
            <Timeline sx={{ zIndex: 1 }}>
              {
                !disableScrollFading && (
                  <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
                )
              }
              <AutoSizer disableHeight>
                {({ width }) => (
                  <ActivityTimeline
                    width={width}
                    height={height}
                    sortedActivityData={sortedActivityData}
                    isItemLoaded={isItemLoaded}
                    itemCount={itemCount}
                    onLoadMoreComments={onLoadMoreComments}
                    listRef={listRef}
                    getRowHeight={getRowHeight}
                    rowHeights={rowHeights}
                    showCondensed={showCondensed}
                    currentUser={currentUser}
                    editModeCommentID={editModeCommentID}
                    handleEnterEditMode={handleEnterEditMode}
                    handleDeleteComment={handleDeleteComment}
                  />
                )}
              </AutoSizer>
              {
                !disableScrollFading && (
                  <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
                )
              }
            </Timeline>
          </div>
        )}

        {!!currentUser && (
          <div className="border-t-2 border-border-light pt-4 flex justify-start items-center w-full">
            <Avatar />
            <TextInputWithError
              type="text"
              ref={inputRef}
              className="ml-2 w-full"
              errorMessage="Please enter at least three characters for your comment."
              placeholder={
                modelDisplayName ? `Leave a comment on ${modelDisplayName}...` : 'Leave a comment...'
              }
              value={messageText}
              onChange={setMessageText}
              onSubmit={onSubmit}
              onEntryValidation={entry => entry?.length > 3}
              block
            />
          </div>
        )}
      </CollapsibleCardView>
    </div>
  );
};
export default ActivityLog;
